import type {
  AssistantMemoryListResponse,
  AssistantMemoryResponse,
  AssistantMessageFeedbackResponse,
  AssistantUsageResponse,
  ConversationResponse,
  CreateConversationRequest,
  FileResponse,
  MessageResponse,
  SaveAssistantMemoryRequest,
  SetMessageFeedbackRequest,
} from 'src/api/ProactApiClient';
import {assistantApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export interface MessageFeedbackTarget {
  conversationId: number;
  messageId: number;
}

export interface SetMessageFeedbackArgs extends MessageFeedbackTarget {
  request: SetMessageFeedbackRequest;
}

export interface AssistantQueryCsvArgs {
  conversationId: number;
  toolUseId: string;
}

export interface UpdateAssistantMemoryArgs {
  id: number;
  request: SaveAssistantMemoryRequest;
}

const MEMORY_LIST_TAG = {type: 'AssistantMemory', id: 'LIST'} as const;

export const assistantApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    listConversations: builder.query<ConversationResponse[], void>({
      queryFn: clientQuery(() => assistantApiClient.listConversations()),
      providesTags: [{type: 'AssistantConversations', id: 'LIST'}],
    }),
    getConversationMessages: builder.query<MessageResponse[], number>({
      queryFn: clientQuery(id => assistantApiClient.getMessages(id)),
      providesTags: (_result, _error, id) => [
        {type: 'AssistantConversations', id},
      ],
    }),
    createConversation: builder.mutation<
      ConversationResponse,
      CreateConversationRequest
    >({
      queryFn: clientQuery(request =>
        assistantApiClient.createConversation(request)
      ),
      invalidatesTags: [{type: 'AssistantConversations', id: 'LIST'}],
    }),
    deleteConversation: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => assistantApiClient.deleteConversation(id)),
      invalidatesTags: (_result, _error, id) => [
        {type: 'AssistantConversations', id: 'LIST'},
        {type: 'AssistantConversations', id},
      ],
    }),
    getAssistantUsage: builder.query<AssistantUsageResponse, void>({
      queryFn: clientQuery(() => assistantApiClient.getUsage()),
      providesTags: [{type: 'AssistantUsage', id: 'CURRENT'}],
    }),
    setMessageFeedback: builder.mutation<
      AssistantMessageFeedbackResponse,
      SetMessageFeedbackArgs
    >({
      queryFn: clientQuery(({messageId, request}) =>
        assistantApiClient.setMessageFeedback(messageId, request)
      ),
      onQueryStarted: async (
        {conversationId, messageId, request},
        {dispatch, queryFulfilled}
      ) => {
        const patch = dispatch(
          patchMessageFeedback(conversationId, messageId, {...request})
        );
        try {
          const {data} = await queryFulfilled;
          dispatch(patchMessageFeedback(conversationId, messageId, data));
        } catch {
          patch.undo();
        }
      },
    }),
    exportAssistantQueryCsv: builder.mutation<
      FileResponse | null,
      AssistantQueryCsvArgs
    >({
      queryFn: clientQuery(({conversationId, toolUseId}) =>
        assistantApiClient.exportQueryCsv(conversationId, toolUseId)
      ),
    }),
    deleteMessageFeedback: builder.mutation<void, MessageFeedbackTarget>({
      queryFn: voidClientQuery(({messageId}) =>
        assistantApiClient.deleteMessageFeedback(messageId)
      ),
      onQueryStarted: async (
        {conversationId, messageId},
        {dispatch, queryFulfilled}
      ) => {
        const patch = dispatch(
          patchMessageFeedback(conversationId, messageId, undefined)
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    getAssistantMemory: builder.query<AssistantMemoryListResponse, void>({
      queryFn: clientQuery(() => assistantApiClient.getMemory()),
      providesTags: [MEMORY_LIST_TAG],
    }),
    setAssistantMemoryEnabled: builder.mutation<void, boolean>({
      queryFn: voidClientQuery(enabled =>
        assistantApiClient.setMemoryEnabled({enabled})
      ),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
    createAssistantMemory: builder.mutation<
      AssistantMemoryResponse,
      SaveAssistantMemoryRequest
    >({
      queryFn: clientQuery(request => assistantApiClient.createMemory(request)),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
    updateAssistantMemory: builder.mutation<
      AssistantMemoryResponse,
      UpdateAssistantMemoryArgs
    >({
      queryFn: clientQuery(({id, request}) =>
        assistantApiClient.updateMemory(id, request)
      ),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
    deleteAssistantMemory: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => assistantApiClient.deleteMemory(id)),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
    deleteAllAssistantMemory: builder.mutation<void, void>({
      queryFn: voidClientQuery(() => assistantApiClient.deleteAllMemory()),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
    acceptAssistantMemory: builder.mutation<AssistantMemoryResponse, number>({
      queryFn: clientQuery(id => assistantApiClient.acceptMemory(id)),
      invalidatesTags: [MEMORY_LIST_TAG],
    }),
  }),
});

const patchMessageFeedback = (
  conversationId: number,
  messageId: number,
  feedback: AssistantMessageFeedbackResponse | undefined
) =>
  assistantApi.util.updateQueryData(
    'getConversationMessages',
    conversationId,
    messages => {
      const message = messages.find(m => m.id === messageId);
      if (message) message.feedback = feedback;
    }
  );

export const invalidateAssistantUsage = () =>
  assistantApi.util.invalidateTags([{type: 'AssistantUsage', id: 'CURRENT'}]);

export const invalidateAssistantMemory = () =>
  assistantApi.util.invalidateTags([MEMORY_LIST_TAG]);

export const {
  useListConversationsQuery,
  useGetConversationMessagesQuery,
  useCreateConversationMutation,
  useDeleteConversationMutation,
  useGetAssistantUsageQuery,
  useSetMessageFeedbackMutation,
  useDeleteMessageFeedbackMutation,
  useExportAssistantQueryCsvMutation,
  useGetAssistantMemoryQuery,
  useSetAssistantMemoryEnabledMutation,
  useCreateAssistantMemoryMutation,
  useUpdateAssistantMemoryMutation,
  useDeleteAssistantMemoryMutation,
  useDeleteAllAssistantMemoryMutation,
  useAcceptAssistantMemoryMutation,
} = assistantApi;
